import { forwardRef, type HTMLAttributes } from 'react'
import { CardFrame } from '@/components/base/CardFrame'
import { BackgroundImage } from '@/components/base/BackgroundImage'
import { Tooltip } from '@/components/base/Tooltip'
import { RetentionHeader } from '@/components/composite/RetentionHeader'
import { ChartIcon } from '@/icons'
import { statsPanelVariants } from './StatsPanel.variants'
import { formatStatAmount } from './utils/formatStatAmount'

export interface StatsPanelTranslations {
  /** Header title */
  title?: string
  /** Header subtitle */
  subtitle?: string
  /** Header tooltip */
  tooltip?: string
  /** "Wagered" label */
  wagered?: string
  /** "Total Bets" label */
  totalBets?: string
  /** "Won" label */
  won?: string
  /** "Not available for play money" tooltip text */
  notAvailable?: string
  /** Tooltip explaining why these totals differ slightly from betting history */
  approximate?: string
}

export interface StatsPanelProps extends HTMLAttributes<HTMLDivElement> {
  /** Total number of bets placed */
  totalBets?: number
  /** Total number of winning bets */
  totalWins?: number
  /** Win rate percentage (0-100) */
  winRate?: number
  /** Total amount wagered in player's currency (minor units, e.g., cents) */
  totalWagered?: number
  /** Total amount won in player's currency (minor units) */
  totalWon?: number
  /** Biggest single win in player's currency (minor units) */
  biggestWin?: number
  /** Player's currency code (e.g., "USD", "THB") */
  currency?: string
  /** Background image URL */
  bgImageUrl?: string
  /** Translation strings */
  translations?: StatsPanelTranslations
}

export const StatsPanel = forwardRef<HTMLDivElement, StatsPanelProps>(
  (
    {
      className,
      totalBets = 0,
      totalWins = 0,
      winRate = 0,
      totalWagered,
      totalWon,
      biggestWin,
      currency,
      bgImageUrl,
      translations,
      ...props
    },
    ref
  ) => {
    const accentColor = 'green'
    const hasCurrencyStats = totalWagered !== undefined && currency !== undefined

    // Use translations with fallbacks
    const t = {
      title: translations?.title ?? 'Stats',
      subtitle: translations?.subtitle ?? 'Your lifetime betting statistics',
      tooltip: translations?.tooltip ?? 'Track your betting performance',
      wagered: translations?.wagered ?? 'Wagered',
      totalBets: translations?.totalBets ?? 'Total Bets',
      won: translations?.won ?? 'Won',
      notAvailable: translations?.notAvailable ?? 'Not available for play money',
      approximate:
        translations?.approximate ?? 'Approximate total — see Bet History for exact amounts'
    }

    return (
      <div className={statsPanelVariants({ accentColor, className })} ref={ref} {...props}>
        {bgImageUrl ? (
          <BackgroundImage
            className="
              opacity-30 h-1/2 rounded-xl
              [-webkit-mask-image:linear-gradient(to_bottom,black_50%,transparent_100%)]
              [mask-image:linear-gradient(to_bottom,black_50%,transparent_100%)]
            "
            imageSrc={bgImageUrl}
          />
        ) : null}
        <div className="absolute inset-0 bg-fade-to-transparent z-10 rounded-xl" />

        <div className="relative z-20 p-2">
          <RetentionHeader
            accentColor={accentColor}
            icon={<ChartIcon className="w-[22px] h-[22px]" />}
            subtitle={t.subtitle}
            title={t.title}
            tooltip={t.tooltip}
          />

          <CardFrame className="mt-4">
            <CardFrame
              accentColor={accentColor}
              className="p-2 flex justify-between bg-glass-overlay-light"
            >
              <div className="flex flex-col text-bold text-white text-base">
                <span className="text-white/75">{t.wagered}</span>
                {hasCurrencyStats ? (
                  <Tooltip
                    position="bottom"
                    trigger={
                      <span className="text-md">
                        {formatStatAmount(totalWagered, currency, { approximate: true })}
                      </span>
                    }
                  >
                    <span className="text-xs">{t.approximate}</span>
                  </Tooltip>
                ) : (
                  <Tooltip
                    position="bottom"
                    trigger={<span className="text-md text-white/50">--</span>}
                  >
                    <span className="text-xs">{t.notAvailable}</span>
                  </Tooltip>
                )}
              </div>

              <div className="flex flex-col text-bold text-white text-base">
                <span className="text-white/75">{t.totalBets}</span>
                <span className="text-md">{totalBets.toLocaleString()}</span>
              </div>

              <div className="flex flex-col text-bold text-white text-base">
                <span className="text-green">{t.won}</span>
                {hasCurrencyStats && totalWon !== undefined ? (
                  <Tooltip
                    position="bottom"
                    trigger={
                      <span className="text-md">
                        {formatStatAmount(totalWon, currency, { approximate: true })}
                      </span>
                    }
                  >
                    <span className="text-xs">{t.approximate}</span>
                  </Tooltip>
                ) : (
                  <Tooltip
                    position="bottom"
                    trigger={<span className="text-md text-white/50">--</span>}
                  >
                    <span className="text-xs">{t.notAvailable}</span>
                  </Tooltip>
                )}
              </div>
            </CardFrame>
          </CardFrame>
        </div>
      </div>
    )
  }
)

StatsPanel.displayName = 'StatsPanel'
